import { useSnackbar } from 'notistack';
import { useState, useEffect } from 'react';
import { ApolloQueryResult } from '@apollo/client';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';

import {
  TrinksStoreIntegrationType,
  useSetTrinksStoreIntegrationMutation,
  SetTrinksStoreIntegrationMutationVariables as TVariables,
} from 'src/graphql/generated';

import FormProvider from 'src/components/hook-form/form-provider';
import { RHFSwitch, RHFTextField } from 'src/components/hook-form';

import {
  TrinksFormSchema,
  trinksConfigFormSchemaDefaultValues,
} from '../schema/trinks-form-schema';

// ----------------------------------------------------------------------

type TAttributes = 'integrationId' | 'apiKey' | 'activated';

type TCurrentData = Pick<TrinksStoreIntegrationType, TAttributes>;

type Props = {
  open: boolean;
  onClose: () => void;
  disableForm?: boolean;
  currentData?: TCurrentData | null;
  refetch: () => Promise<ApolloQueryResult<any>>;
};

// ----------------------------------------------------------------------

export function ConfigTrinksModal({
  open,
  onClose,
  currentData,
  disableForm = false,
  refetch,
}: Props) {
  const [enableForm, setEnableForm] = useState<boolean>(!disableForm);
  const [setTrinksIntegration] = useSetTrinksStoreIntegrationMutation();
  const { enqueueSnackbar } = useSnackbar();

  const methods = useForm({
    resolver: yupResolver(TrinksFormSchema),
    defaultValues: trinksConfigFormSchemaDefaultValues,
  });

  const watch = useWatch({ control: methods.control });

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = methods;

  const handleClose = () => {
    onClose();
    reset();
    setEnableForm(!disableForm);
  };

  const onSubmit = handleSubmit(async (data) => {
    const { integrationId, apiKey, activated } = data;

    const variables: TVariables = {
      input: {
        integrationId,
        apiKey,
        activated,
      },
    };

    try {
      const { data: response } = await setTrinksIntegration({ variables });

      if (response?.setTrinksStoreIntegration?.ok === true) {
        enqueueSnackbar('Cadastro efetuado com sucesso!', { variant: 'success' });
      } else {
        enqueueSnackbar('Ocorreu um erro, tente mais tarde!', { variant: 'warning' });
      }

      setEnableForm(false);
      onClose();
      await refetch();
    } catch (error) {
      if (error?.message) {
        enqueueSnackbar(error?.message, { variant: 'error' });

        return;
      }
      enqueueSnackbar('Não foi possível atualizar o dados!', { variant: 'error' });
    }
  });

  useEffect(() => {
    const updateFields = () => {
      if (!currentData) return;

      const { integrationId, apiKey, activated } = currentData;

      reset({
        integrationId,
        apiKey,
        activated,
      });
    };

    updateFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentData]);

  useEffect(() => {
    setEnableForm(!disableForm);
  }, [disableForm]);

  return (
    <FormProvider methods={methods}>
      <Dialog
        open={open}
        PaperProps={{
          sx: {
            minWidth: { xs: '90%', md: 600 },
          },
        }}
      >
        <DialogTitle textAlign="center" fontSize={20}>
          Integração Trinks
        </DialogTitle>

        <DialogContent
          sx={{
            minWidth: { xs: 300, md: 600 },
            minHeight: 180,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 2,
            py: 2,
          }}
        >
          <RHFTextField
            name="integrationId"
            label="ID de Integração"
            disabled={!enableForm}
            helperText="Corresponde ao ID da Loja na Trinks. Este campo será integrado com o ID de Integração."
          />

          <RHFTextField
            name="apiKey"
            label="Chave da API (API Key)"
            disabled={!enableForm}
            helperText="Token indicado pela Trinks."
          />
        </DialogContent>

        <Divider sx={{ borderStyle: 'dashed' }} />
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <RHFSwitch
            name="activated"
            label={watch.activated ? 'Serviço Ativo' : 'Serviço Inativo'}
            disabled={!enableForm}
          />
          <Stack flexDirection="row" spacing={2}>
            <Button variant="outlined" color="inherit" onClick={handleClose}>
              Fechar
            </Button>
            {enableForm && (
              <LoadingButton
                variant="contained"
                color="primary"
                type="submit"
                loading={isSubmitting}
                onClick={onSubmit}
              >
                Salvar
              </LoadingButton>
            )}
            {!enableForm && (
              <Button variant="contained" color="primary" onClick={() => setEnableForm(true)}>
                {currentData ? 'Editar' : 'Criar'}
              </Button>
            )}
          </Stack>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
